import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Typography, TextField, List, ListItem, IconButton, Tooltip } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TenTabStrategyDialog from './TenTabStrategyDialog';

import PropTypes from 'prop-types';

function MinimalMultiTabDialog({ open, onClose, onRun }) {
  const [tab, setTab] = useState(0);
  const [studies, setStudies] = useState([]);
  const [harmonicForm, setHarmonicForm] = useState({ harmonic: '', planet: '' });
  const [midpointForm, setMidpointForm] = useState({ planet1: '', planet2: '' });

  // Add handlers for each form
  const handleAddHarmonic = () => {
    setStudies([...studies, { type: 'harmonic', ...harmonicForm }]);
    setHarmonicForm({ harmonic: '', planet: '' });
  };
  const handleAddMidpoint = () => {
    setStudies([...studies, { type: 'midpoint', ...midpointForm }]);
    setMidpointForm({ planet1: '', planet2: '' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Multi-Tab Strategy Dialog (Minimal Example)</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={3}>
          {/* Left: Tabs and TabPanels */}
          <Box flex={2}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Harmonic Study" />
              <Tab label="Midpoint Study" />
            </Tabs>
            {tab === 0 && (
              <Box mt={2}>
                <Typography>Harmonic Study Form</Typography>
                <TextField
                  label="Harmonic"
                  value={harmonicForm.harmonic}
                  onChange={e => setHarmonicForm(f => ({ ...f, harmonic: e.target.value }))}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <TextField
                  label="Planet"
                  value={harmonicForm.planet}
                  onChange={e => setHarmonicForm(f => ({ ...f, planet: e.target.value }))}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <Button onClick={handleAddHarmonic} disabled={!harmonicForm.harmonic || !harmonicForm.planet}>
                  Add
                </Button>
              </Box>
            )}
            {tab === 1 && (
              <Box mt={2}>
                <Typography>Midpoint Study Form</Typography>
                <TextField
                  label="Planet 1"
                  value={midpointForm.planet1}
                  onChange={e => setMidpointForm(f => ({ ...f, planet1: e.target.value }))}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <TextField
                  label="Planet 2"
                  value={midpointForm.planet2}
                  onChange={e => setMidpointForm(f => ({ ...f, planet2: e.target.value }))}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <Button onClick={handleAddMidpoint} disabled={!midpointForm.planet1 || !midpointForm.planet2}>
                  Add
                </Button>
              </Box>
            )}
          </Box>
          {/* Right: Persistent List */}
          <Box flex={1} minWidth={300}>
            <Typography fontWeight="bold" mb={1}>Selected Strategy Components</Typography>
            <List sx={{ border: '1px solid #222', minHeight: 200, bgcolor: '#fff' }}>
              {studies.length === 0 ? (
                <ListItem>
                  <Typography color="textSecondary">No components added</Typography>
                </ListItem>
              ) : (
                studies.map((study, idx) => (
                  <ListItem key={idx}>
                    {study.type === 'harmonic'
                      ? `Harmonic: ${study.harmonic} of ${study.planet}`
                      : `Midpoint: ${study.planet1} / ${study.planet2}`}
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={() => onRun(studies)}
          disabled={studies.length === 0}
          variant="contained"
          color="primary"
        >
          Run
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Demo icon trigger component
export function MinimalMultiTabDialogTrigger() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="Open Multi-Tab Strategy Dialog">
        <IconButton onClick={() => setOpen(true)}>
          <MenuBookIcon sx={{ color: '#1976d2' }} />
        </IconButton>
      </Tooltip>
      <TenTabStrategyDialog
        open={open}
        onClose={() => setOpen(false)}
        onRun={studies => {
          alert('Run studies: ' + JSON.stringify(studies, null, 2));
          setOpen(false);
        }}
      />
    </>
  );
}

MinimalMultiTabDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
};

export default MinimalMultiTabDialog;
